import './About.css';

const features = [
  {
    title: 'Real-time Logic App monitoring',
    desc: 'See the status of every Logic App run the moment it happens. Succeeded, failed, running — all visible in one dashboard with live run counts and failure rates.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
  },
  {
    title: 'Failed run diagnostics',
    desc: 'Drill into any failed run and see exactly which action broke, what the input was, and what error was returned — without opening the Azure Portal.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
  },
  {
    title: 'API documentation hub',
    desc: 'Upload OpenAPI specs and render them instantly with Swagger UI or Scalar. Organized by group, searchable, and always up to date — all in one place.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
  },
  {
    title: 'Shareable public links',
    desc: 'Share API documentation with external developers or stakeholders in one click. The recipient gets a public link — no Azure login, no onboarding needed.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
      </svg>
    ),
  },
  {
    title: 'Group & organize APIs',
    desc: 'Group API specs by team, domain, or product area. With dozens or hundreds of specs, a flat list doesn\'t scale — structured grouping keeps navigation fast and clear.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    title: 'Azure-native, zero infrastructure',
    desc: 'Built entirely on Azure Functions, Blob Storage, and Table Storage. No servers to manage, no databases to maintain. Scales automatically and costs nothing when idle.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
      </svg>
    ),
  },
];

const stats = [
  { value: '20+',     label: 'Logic Apps monitored in real-time' },
  { value: '99%',     label: 'average run success rate' },
  { value: '10+',     label: 'BC API specs published & documented' },
  { value: '100%',    label: 'of specs publicly shareable' },
];

const steps = [
  {
    number: '01',
    title: 'Monitor your Logic Apps',
    desc: 'Connect your Azure subscription and see all Logic App runs in real-time. Failed runs surface immediately with full action-level diagnostics — no Azure Portal needed.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Publish your API docs',
    desc: 'Upload an OpenAPI YAML spec, give it a name and an optional group. It\'s immediately rendered with Swagger UI and Scalar — ready to view and navigate.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Share with one click',
    desc: 'Hit Share on any API spec to get a public URL. Anyone with the link can view the full interactive documentation — no login, no account, just the docs.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
      </svg>
    ),
  },
];

function About() {
  return (
    <div className="about-page">

      {/* ── Hero ──────────────────────────────────── */}
      <div className="about-hero">
        <h1 className="about-hero-title">
          All your integrations,<br />
          <span>one place.</span>
        </h1>
        <p className="about-hero-body">
          The Integration Hub gives you real-time visibility into your Azure Logic Apps and
          a central place to publish, organise, and share API documentation. No scattered
          dashboards, no digging through the Azure Portal — everything your team needs
          to monitor and communicate integrations, right here.
        </p>
      </div>

      {/* ── Stats strip ───────────────────────────── */}
      <div className="about-stats">
        {stats.map(s => (
          <div key={s.label} className="about-stat">
            <span className="about-stat-value">{s.value}</span>
            <span className="about-stat-label">{s.label}</span>
          </div>
        ))}
      </div>


      {/* ── Features ──────────────────────────────── */}
      <div className="about-features">
        <p className="about-section-label">What it does</p>
        <h2 className="about-section-title">Everything you need to run and document integrations</h2>
        <div className="about-features-grid">
          {features.map(f => (
            <div key={f.title} className="about-feature-card">
              <div className="about-feature-icon">{f.icon}</div>
              <h3 className="about-feature-title">{f.title}</h3>
              <p className="about-feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── How it works ──────────────────────────── */}
      <div className="about-steps">
        <p className="about-section-label">How it works</p>
        <h2 className="about-section-title">From monitoring to sharing in three steps</h2>
        <div className="about-steps-row">
          {steps.map(s => (
            <div key={s.number} className="about-step">
              <div className="about-step-icon">{s.icon}</div>
              <span className="about-step-number">{s.number}</span>
              <h3 className="about-step-title">{s.title}</h3>
              <p className="about-step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Logic Apps deep dive ──────────────────── */}
      <div className="about-renewal">
        <p className="about-section-label">Logic App monitoring</p>
        <h2 className="about-section-title">All your Logic App runs, without the portal</h2>
        <div className="about-security-grid">
          <div className="about-security-card">
            <h3 className="about-security-card-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'1.2em',height:'1.2em',display:'inline',marginRight:'0.5em',verticalAlign:'middle'}}>
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
              Live run dashboard
            </h3>
            <p className="about-security-card-body">
              Every Logic App, its run count, success rate, and latest status visible at a glance.
              No navigating subscriptions, resource groups, or run history — the full picture is
              on the dashboard the moment you open the hub.
            </p>
          </div>
          <div className="about-security-card">
            <h3 className="about-security-card-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'1.2em',height:'1.2em',display:'inline',marginRight:'0.5em',verticalAlign:'middle'}}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Instant failure triage
            </h3>
            <p className="about-security-card-body">
              Failed runs surface immediately with timestamps and trigger details. No filtering,
              no searching — failures are front and centre so you can start diagnosing the moment
              something breaks in production.
            </p>
          </div>
          <div className="about-security-card">
            <h3 className="about-security-card-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'1.2em',height:'1.2em',display:'inline',marginRight:'0.5em',verticalAlign:'middle'}}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              Action-level diagnostics
            </h3>
            <p className="about-security-card-body">
              Click into any run and see every action — its status, input, and output.
              Pinpoint exactly which step failed and why in two clicks, without opening
              a single Azure Portal screen.
            </p>
          </div>
          <div className="about-security-card">
            <h3 className="about-security-card-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'1.2em',height:'1.2em',display:'inline',marginRight:'0.5em',verticalAlign:'middle'}}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              No per-user permissions
            </h3>
            <p className="about-security-card-body">
              The hub connects through a single service principal. Your entire team sees
              the run data and diagnostics they need without individual Azure role
              assignments or portal access.
            </p>
          </div>
        </div>
      </div>

      {/* ── API docs deep dive ────────────────────── */}
      <div className="about-security">
        <p className="about-section-label">API documentation</p>
        <h2 className="about-section-title">Your API docs, where your team needs them</h2>
        <div className="about-security-grid">
          <div className="about-security-card">
            <h3 className="about-security-card-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'1.2em',height:'1.2em',display:'inline',marginRight:'0.5em',verticalAlign:'middle'}}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              OpenAPI-first
            </h3>
            <p className="about-security-card-body">
              Paste any valid OpenAPI 3.0 YAML spec and it's published instantly. No conversion,
              no build step. The spec is stored in Azure Blob Storage and served directly to
              Swagger UI and Scalar.
            </p>
          </div>
          <div className="about-security-card">
            <h3 className="about-security-card-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'1.2em',height:'1.2em',display:'inline',marginRight:'0.5em',verticalAlign:'middle'}}>
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
              </svg>
              Grouped & searchable
            </h3>
            <p className="about-security-card-body">
              Assign any spec to a group — Finance, Operations, Platform, or whatever fits your
              structure. The sidebar stays navigable at scale with collapsible sections and
              instant search across all specs.
            </p>
          </div>
          <div className="about-security-card">
            <h3 className="about-security-card-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'1.2em',height:'1.2em',display:'inline',marginRight:'0.5em',verticalAlign:'middle'}}>
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              Public share links
            </h3>
            <p className="about-security-card-body">
              Every spec gets a permanent public URL generated at creation. Share it with
              a partner, a new developer, or a client — they see the full interactive docs
              without any login or Azure account.
            </p>
          </div>
          <div className="about-security-card">
            <h3 className="about-security-card-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'1.2em',height:'1.2em',display:'inline',marginRight:'0.5em',verticalAlign:'middle'}}>
                <polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/>
              </svg>
              Two renderers, one spec
            </h3>
            <p className="about-security-card-body">
              Toggle between Swagger UI for familiar, structured browsing, and Scalar for a
              cleaner modern view. The same spec renders in both — pick whatever your audience
              prefers.
            </p>
          </div>
        </div>
      </div>

      {/* ── Tech ──────────────────────────────────── */}
      <div className="about-tech">
        <p className="about-section-label">Under the hood</p>
        <h2 className="about-section-title">Simple architecture, built to last</h2>
        <div className="about-tech-grid">
          <div className="about-tech-card">
            <h3 className="about-tech-card-title">Azure Functions backend</h3>
            <p className="about-tech-card-body">
              Separate functions for Logic App runs, spec management, and share links — each
              with a single responsibility. Managed identities handle all Azure service
              authentication. No passwords, no connection strings stored anywhere.
            </p>
          </div>
          <div className="about-tech-card">
            <h3 className="about-tech-card-title">Azure Blob + Table Storage</h3>
            <p className="about-tech-card-body">
              API specs are stored as YAML blobs in Azure Blob Storage. Metadata —
              name, group, share ID — lives in Azure Table Storage for instant lookup.
              Share links resolve to specs in a single table read.
            </p>
          </div>
          <div className="about-tech-card">
            <h3 className="about-tech-card-title">React frontend with Azure AD</h3>
            <p className="about-tech-card-body">
              The portal is gated behind Azure AD (MSAL). Only authenticated users can
              manage Logic Apps and API docs. Public share links are the only exception —
              they bypass auth intentionally so external parties can view docs.
            </p>
          </div>
          <div className="about-tech-card">
            <h3 className="about-tech-card-title">Stack</h3>
            <p className="about-tech-card-body">
              Backend: <strong>.NET 8 Azure Functions (Isolated)</strong> —
              Frontend: <strong>React 18 + MSAL</strong> —
              Storage: <strong>Azure Blob & Table Storage</strong> —
              Auth: <strong>Azure AD + Managed Identities</strong>
            </p>
          </div>
        </div>
      </div>

      {/* ── Coming soon ───────────────────────────── */}
      <div className="about-coming-soon">
        <div className="about-coming-soon-eyebrow">
          <span className="about-coming-soon-badge">Coming soon</span>
        </div>
        <h2 className="about-coming-soon-title">The hub keeps growing</h2>
        <p className="about-coming-soon-body">
          Logic Apps and API documentation are the foundation. The Integration Hub is built
          to expand — every new module brings the same visibility and one-click sharing to
          another part of Astena's integration landscape.
        </p>
        <div className="about-coming-soon-grid">
          <div className="about-coming-soon-card">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            <h4>Functions monitoring</h4>
            <p>Execution counts, failure rates, and duration trends for all your Azure Functions — the same visibility Logic Apps already has.</p>
          </div>
          <div className="about-coming-soon-card">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <h4>Failure alerting</h4>
            <p>Proactive notifications when integrations fail — before your users notice. Teams, email, or webhook.</p>
          </div>
          <div className="about-coming-soon-card">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            <h4>Business Central integrations</h4>
            <p>Monitor and document the integrations connecting Business Central to the rest of your landscape — all from one place.</p>
          </div>
          <div className="about-coming-soon-card">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/>
              <path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10A15.3 15.3 0 0 1 8 12a15.3 15.3 0 0 1 4-10z"/>
            </svg>
            <h4>Portal & app monitoring</h4>
            <p>Uptime, response times, and health checks for your web portals and internal apps — integrated into the same hub.</p>
          </div>
        </div>
      </div>

    </div>
  );
}

export default About;
